import type { ResolvedComponent } from "@inertiajs/react"
import { createInertiaApp } from "@inertiajs/react"
import { type ReactNode, StrictMode } from "react"
import { createRoot } from "react-dom/client"

import AppPostHogProvider from "@/components/posthog-provider"
import PersistentLayout from "@/layouts/persistent-layout"
import { QueryProvider } from "@/providers/query-provider"
import type { SharedData } from "@/types"

const appName = import.meta.env.VITE_APP_NAME ?? "The Book Cove"
const pages = import.meta.glob<{ default: ResolvedComponent }>(
  "../pages/**/*.tsx",
)

if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js", { scope: "/" })
      .catch((error) => {
        console.error("Service worker registration failed:", error)
      })
  })
}

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  title: (title) => (title ? `${title} - ${appName}` : appName),

  resolve: async (name) => {
    const pageResolver = pages[`../pages/${name}.tsx`]

    if (!pageResolver) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
      throw new Error(`Missing Inertia page component: '${name}.tsx'`)
    }

    const page = await pageResolver()

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    page.default.layout ??= (page: ReactNode) => (
      <PersistentLayout>{page}</PersistentLayout>
    )

    return page.default
  },

  setup({ el, App, props }) {
    const posthogConfig = (props.initialPage.props as unknown as SharedData)
      .posthog

    // Uncomment the following to enable SSR hydration:
    // if (el.hasChildNodes()) {
    //   hydrateRoot(el, <App {...props} />)
    //   return
    // }
    createRoot(el).render(
      <StrictMode>
        <AppPostHogProvider config={posthogConfig}>
          <QueryProvider>
            <App {...props} />
          </QueryProvider>
        </AppPostHogProvider>
      </StrictMode>,
    )
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: true,
    },
    prefetch: {
      cacheFor: "5m",
      hoverDelay: 50,
    },
  },

  progress: {
    color: "#4B5563",
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
    )
  }
})
